import React, { useState } from 'react'
import { FunctionsList, ItemDetails, PeopleList } from 'components'
import { useIsSmallScreen } from 'packages/core'
import { Box, styled, Typography } from '@mui/material'
import { Pagination, ThresholdSearchInput } from 'packages/eid-ui'
import {
    useAzureRoleGlobalSensitiveFunctions,
    useAzureRoleBr,
    useTargetPerson,
    useApplicationOwners,
    useResourceApprovers,
    useResponsibleParty,
} from 'hooks'
import { useTranslation } from 'react-i18next'
import cartHelpers from 'containers/Cart/cartHelpers'
import { CommonAttributes } from '../../CommonAttributes'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { RenewRevoke } from 'components/RenewRevoke'
import { Skeleton } from '@mui/material'
import { useHistory } from 'react-router'
import useSubcomponents from 'useSubcomponents'
import LocalSensitiveFunctions from 'components/Local Sensitive Functions/LocalSensitiveFunctions'
import ActivateNowButton from 'containers/ListingPage/ActivateNowButton'
import useConfiguration from 'useConfiguration'
import GenericExtendButton from 'components/DynamicComponents/GenericExtendButton'
import { PersonDetails } from 'components/ApplicationBanner/PersonDetails'

const hasAccessToResponsiblePartyControl =
    'ITShop-AzureRoles-Details-ResponsibleParty-Control'

const PaddedDiv = styled('div')({
    padding: '0 31px',
})

const GlobalSensitiveFunctions = ({ azureAdminRole }) => {
    const take = 10
    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreen()

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }

    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data } = useAzureRoleGlobalSensitiveFunctions(
        azureAdminRole.id,
        (page - 1) * perPageItems,
        perPageItems,
        null,
        searchKey && encodeURIComponent(searchKey),
    )

    const globalSensitiveFunctions = data ? data.data : undefined

    const globalFunctionsLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    return (
        <>
            <PaddedDiv
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 31px',
                }}
            >
                <Box width="50%">
                    <Typography
                        style={{
                            fontSize: '12px',
                            lineHeight: 1.33,
                            color: '#9b9b9b',
                            wordBreak: 'break-word',
                            textTransform: 'uppercase',
                            minWidth: '80px',
                        }}
                    >
                        {t('Common_GlobalFunctionalAccess')}
                    </Typography>
                </Box>

                <Box width={!isSmallScreen ? '308px' : '50%'} minWidth="130px">
                    <ThresholdSearchInput
                        placeholder={'Common_Search'}
                        handleSearch={handleSearchChange}
                    />
                </Box>
            </PaddedDiv>

            {globalFunctionsLoading ? (
                <ItemDetails.AttributesContainer>
                    <PaddedDiv>
                        <ItemDetails.AttributesLoader />
                    </PaddedDiv>
                </ItemDetails.AttributesContainer>
            ) : (
                <>
                    {globalSensitiveFunctions && (
                        <>
                            <Box overflow="auto">
                                <FunctionsList
                                    data={globalSensitiveFunctions}
                                    loading={globalFunctionsLoading}
                                />

                                <Box
                                    padding={
                                        !isSmallScreen ? '20px 32px' : '16px'
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent={
                                        !isSmallScreen ? 'flex-start' : 'center'
                                    }
                                >
                                    <Pagination
                                        count={numberOfPages}
                                        size={
                                            !isSmallScreen ? 'large' : 'small'
                                        }
                                        page={page}
                                        onChange={handlePageChange}
                                        totalCount={data?.totalCount}
                                        onPageSelection={handlePageSelection}
                                        itemsPerPage={perPageItems}
                                        onItemsPerPageChange={
                                            handleItemsPerPageChange
                                        }
                                        showPageSelection={true}
                                        showItemsPerPageSelection
                                        showCountStat
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                </>
            )}
        </>
    )
}

const AdditionalInformationSection = ({ item }) => {
    const { t } = useTranslation()

    const { hasAccessToLocalSensitiveFunctionsGrid, hasAccessToControl } =
        useSubcomponents()

    const rbacRole = item.resource

    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'

    const { data: owners, isInitialLoading: ownersLoading } =
        useApplicationOwners(rbacRole.id)

    const { data: approvers, isInitialLoading: approversLoading } =
        useResourceApprovers(rbacRole.id)

    const {
        data: responsibleParty,
        isInitialLoading: responsiblePartyLoading,
    } = useResponsibleParty(
        rbacRole.id,
        Boolean(hasAccessToControl(hasAccessToResponsiblePartyControl)),
    )

    return (
        <>
            {ownersLoading || approversLoading || responsiblePartyLoading ? (
                <ItemDetails.AttributesContainer>
                    <PaddedDiv>
                        <ItemDetails.AttributesLoader />
                    </PaddedDiv>
                </ItemDetails.AttributesContainer>
            ) : (
                <ItemDetails.AttributesContainer bottomPadding="0">
                    <PaddedDiv>
                        <ItemDetails.Attribute
                            label={t('Common_Owners')}
                            value={<PeopleList list={owners ?? []} />}
                            orientation={attributeOrientation}
                        />
                        <ItemDetails.Attribute
                            label={t('Common_Approvers')}
                            value={<PeopleList list={approvers ?? []} />}
                            orientation={attributeOrientation}
                        />
                        <ItemDetails.Attribute
                            label={t('Applications_Owner')}
                            value={
                                responsibleParty ? (
                                    <PersonDetails
                                        size="medium"
                                        person={{
                                            imageThumbUrl:
                                                responsibleParty.imageThumbUrl,
                                            friendlyName:
                                                responsibleParty.friendlyName,
                                            email: responsibleParty.email,
                                            telephone:
                                                responsibleParty.telephone,
                                        }}
                                    />
                                ) : (
                                    <>-</>
                                )
                            }
                            orientation={attributeOrientation}
                        />
                    </PaddedDiv>

                    {hasAccessToLocalSensitiveFunctionsGrid && (
                        <LocalSensitiveFunctions
                            rbacRole={rbacRole}
                            protectSubcomponent={
                                hasAccessToLocalSensitiveFunctionsGrid
                            }
                        />
                    )}
                </ItemDetails.AttributesContainer>
            )}
        </>
    )
}
const OverviewSection = ({ item }) => {
    const { t } = useTranslation()

    const azureAdminRole = item.resource

    const [targetPerson] = useTargetPerson()

    const { data: businessRequestItem, isInitialLoading } = useAzureRoleBr(
        targetPerson.id,
        item.assignment.resourceAssignment.resourceId,
        item.assignment.resourceAssignment.resourceTypeId,
    )

    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'

    return (
        <ItemDetails.AttributesContainer bottomPadding="0">
            <PaddedDiv>
                <ItemDetails.Attribute
                    label={t('Common_SystemType')}
                    value={azureAdminRole.resourceSystemTypeFriendlyName}
                    orientation={attributeOrientation}
                />
                <ItemDetails.Attribute
                    label={t('Common_RoleType')}
                    value={azureAdminRole.roleTypeFriendlyName}
                    orientation={attributeOrientation}
                />
                {azureAdminRole.resourceSystemFriendlyName && (
                    <ItemDetails.Attribute
                        label={t('Common_Tenant')}
                        value={azureAdminRole.resourceSystemFriendlyName}
                        orientation={attributeOrientation}
                    />
                )}
                <ItemDetails.Attribute
                    label={t('Common_Description')}
                    value={azureAdminRole.description}
                    orientation={attributeOrientation}
                />
                {isInitialLoading && <Skeleton height={32} />}
                <CommonAttributes
                    item={item}
                    businessRequestItem={businessRequestItem}
                />
            </PaddedDiv>

            <GlobalSensitiveFunctions azureAdminRole={azureAdminRole} />
        </ItemDetails.AttributesContainer>
    )
}

const AzureAdminRoleDetails = ({ item, toggleDrawer }) => {
    const { t } = useTranslation()

    const history = useHistory()
    const isManageAccess =
        history.location.pathname.indexOf('/manageAccess') >= 0
    const registry = useRegistry()

    const azureAdminRoleType = registry.get(
        ResourceTypesNamespace,
        'AzureAdminRoles',
    )

    const { getResourceType } = useConfiguration()
    const currentResourceType = getResourceType('AzureRoles')

    const [activeTab, setActiveTab] = useState('overview')

    const [targetPerson] = useTargetPerson()

    const preRevoke = () => {
        const itemToAdd = cartHelpers.azAdminRoleToCartItem({
            targetPerson,
            assignmentType: 'Remove',
            azureAdminRole: item.resource,
            assignmentStatus: item.assignment,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    return (
        <>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                />
                <ItemDetails.Tabs.Tab
                    value="additionalInformation"
                    label={t('Common_MoreInformation')}
                />
            </ItemDetails.Tabs>

            {activeTab === 'overview' && <OverviewSection item={item} />}
            {activeTab === 'additionalInformation' && (
                <AdditionalInformationSection item={item} />
            )}
            {isManageAccess && (
                <>
                    <Box
                        display="flex"
                        padding="0 31px"
                        margin="16px 0px"
                        gap="10px"
                    >
                        {item?.assignment.canActivateNow ? (
                            <ActivateNowButton
                                resource={item.resource}
                                resourceType={currentResourceType}
                                forDetailsDrawer={true}
                                width="300px"
                                assignment={item?.assignment}
                            />
                        ) : (
                            <RenewRevoke
                                resourceType={azureAdminRoleType}
                                resource={item.resource}
                                assignment={item.assignment}
                                postRenew={toggleDrawer}
                                preRevoke={preRevoke}
                                postRevoke={toggleDrawer}
                                item={item}
                            />
                        )}
                        {item?.assignment?.isExtendible && (
                            <GenericExtendButton
                                item={item}
                                style={{ minWidth: '300px', height: '50px' }}
                            />
                        )}
                    </Box>
                </>
            )}
        </>
    )
}

export default AzureAdminRoleDetails
